/**
 * Pairing payload — encode/decode room connection info for QR pairing.
 *
 * Format: URL-safe base64 of JSON payload → appended to join URL fragment.
 * Payload includes: signalingUrl, roomId, secret, callerAddress, chain, timestamp.
 * TTL: 5 minutes from creation.
 */

const PAYLOAD_TTL_MS = 5 * 60 * 1000; // 5 minutes

export interface PairingPayload {
  signalingUrl: string;
  roomId: string;
  secret: string;
  callerAddress: string;
  chain: string;
  timestamp: number;
}

export function encodePairingPayload(payload: PairingPayload): string {
  const json = JSON.stringify(payload);
  // URL-safe base64 encoding
  const base64 = btoa(json)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
  return base64;
}

export function decodePairingPayload(encoded: string): PairingPayload {
  try {
    // Restore standard base64
    let base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
    // Add padding
    while (base64.length % 4 !== 0) {
      base64 += '=';
    }
    const json = atob(base64);
    const payload = JSON.parse(json) as unknown;

    if (!isValidPayload(payload)) {
      throw new PairingError('INVALID_PAYLOAD', 'Malformed pairing payload');
    }

    return payload;
  } catch (err) {
    if (err instanceof PairingError) throw err;
    throw new PairingError('DECODE_FAILED', 'Failed to decode pairing payload');
  }
}

export function validatePairingPayload(payload: PairingPayload, expectedChain?: string): void {
  // Check expiry
  const age = Date.now() - payload.timestamp;
  if (age > PAYLOAD_TTL_MS) {
    throw new PairingError('EXPIRED', 'This pairing link has expired. Ask the caller to create a new one.');
  }

  if (age < -60_000) {
    // Clock skew tolerance of 1 minute
    throw new PairingError('CLOCK_SKEW', 'Pairing link timestamp is in the future. Check device clock.');
  }

  // Check chain match
  if (expectedChain && payload.chain !== expectedChain) {
    throw new PairingError(
      'CHAIN_MISMATCH',
      `This link is for ${payload.chain} but you're on ${expectedChain}`
    );
  }

  // Validate URL
  try {
    new URL(payload.signalingUrl);
  } catch {
    throw new PairingError('INVALID_URL', 'Signaling URL is invalid');
  }

  // Validate fields
  if (!payload.roomId || !payload.secret || !payload.callerAddress) {
    throw new PairingError('INVALID_PAYLOAD', 'Missing required pairing fields');
  }
}

export function buildJoinUrl(baseUrl: string, payload: PairingPayload): string {
  const encoded = encodePairingPayload(payload);
  return `${baseUrl}/join#${encoded}`;
}

export function parseJoinUrl(url: string): PairingPayload | null {
  try {
    const hashIndex = url.indexOf('#');
    if (hashIndex === -1) return null;
    const encoded = url.slice(hashIndex + 1);
    if (!encoded) return null;
    return decodePairingPayload(encoded);
  } catch {
    return null;
  }
}

function isValidPayload(value: unknown): value is PairingPayload {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj['signalingUrl'] === 'string' &&
    typeof obj['roomId'] === 'string' &&
    typeof obj['secret'] === 'string' &&
    typeof obj['callerAddress'] === 'string' &&
    typeof obj['chain'] === 'string' &&
    typeof obj['timestamp'] === 'number'
  );
}

export class PairingError extends Error {
  constructor(
    public readonly code: 'INVALID_PAYLOAD' | 'DECODE_FAILED' | 'EXPIRED' | 'CLOCK_SKEW' | 'CHAIN_MISMATCH' | 'INVALID_URL',
    message: string
  ) {
    super(message);
    this.name = 'PairingError';
  }
}
